import React, { useContext } from 'react';

import { Button, Link, Spinner } from '@ergeon/core-components';
import {
  PauseIcon,
  PlayPauseIcon,
  MicrophoneIcon,
  PhoneXMarkIcon,
  HashtagIcon,
} from '@ergeon/icons/hero/micro';
import { useLocation } from 'react-router-dom';

import { TwilioContext } from '../../context-providers/TwilioContext';
import useCallControls from '../../hooks/useCallControls';
import { buildModalUrl, ModalName } from '../../routes';
import { CallWithDuration } from '../../types';

/**
 * Smart hook that provides call control buttons for active calls.
 * Accesses TwilioContext and useCallControls directly to get state and callbacks.
 */
export const useCallControlButtons = (): React.JSX.Element[] => {
  const location = useLocation();
  const currentPath = location.pathname + location.search;

  const { currentCall } = useContext(TwilioContext);

  const {
    isMuted,
    isHolded,
    loadingStates,
    hangupCooldown,
    hangUp,
    toggleMute,
    holdCall,
    holdPending,
  } = useCallControls(currentCall as CallWithDuration);

  return [
    <Button
      aria-label="End current call"
      disabled={loadingStates.hangup || hangupCooldown}
      flavor="danger"
      key="hangup"
      onClick={hangUp}
      size="small"
      title="Hang Up">
      <PhoneXMarkIcon className="size-4 shrink-0" /> Hang Up
    </Button>,
    <Button
      aria-label={isHolded ? 'Unhold call' : 'Hold call'}
      data-testid="hold-call-button"
      disabled={loadingStates.hold || holdPending}
      flavor="regular"
      key="hold"
      onClick={holdCall}
      size="small"
      taste="line"
      title={holdPending ? 'Hold engaging...' : 'Hold Call'}>
      {holdPending ? (
        <span data-testid="hold-pending-spinner">
          <Spinner active className="size-4 shrink-0" color="gray" size={16} />
        </span>
      ) : (
        <>
          {isHolded ? <PlayPauseIcon className="size-4 shrink-0" /> : <PauseIcon className="size-4 shrink-0" />}
          {isHolded ? 'Resume Call' : 'Hold Call'}
        </>
      )}
    </Button>,
    <Button
      aria-label={isMuted ? 'Unmute call' : 'Mute call'}
      disabled={loadingStates.mute}
      flavor={isMuted ? 'danger' : 'regular'}
      key="mute"
      onClick={toggleMute}
      size="small"
      taste="line"
      title={isMuted ? 'Unmute' : 'Mute'}>
      <MicrophoneIcon className="size-4 shrink-0" />
      {isMuted ? 'Unmute' : 'Mute'}
    </Button>,
    <Link className="contents [&>span]:contents" href={buildModalUrl(currentPath, { modal: ModalName.DTMF })} key="dtmf" unstyled>
      <Button
        aria-label="Open DTMF dialer"
        flavor="regular"
        size="small"
        taste="line"
        title="Send DTMF tones">
        <HashtagIcon className="size-4 shrink-0" />
        DTMF Dialer
      </Button>
    </Link>,
  ];
};
